/**
 * Intent filter slice for buyer intent signals.
 * Topics are the primary filter; other filters provide refinement.
 */

import type { CachedTopic } from "@/types/intent";

// Re-export CachedTopic for backwards compatibility
export type { CachedTopic };

/** Maximum number of topics allowed to be selected. */
export const MAX_INTENT_TOPICS = 500;

/**
 * Above this many selected topics, we stop querying "suggested topics" to avoid
 * generating heavy queries / UI noise when the selection set is already large.
 *
 * This is intentionally lower than `MAX_INTENT_TOPICS`.
 */
export const MAX_INTENT_TOPICS_FOR_SUGGESTIONS = 50;

export type IntentSlice = {
  // Topics (Required for other filters to be meaningful)
  topicIds: string[];
  topicsInclusionMethod: "ANY_PRESENT" | "ALL_PRESENT";

  // Optional: Selected intent collection (UI-only, not persisted to API)
  collectionId: string | null;
  collectionName: string | null;

  // Signal Strength
  signalScore: "HIGH" | "MEDIUM"; // HIGH=85+, MEDIUM=65+

  // Buyer's Journey
  buyersJourneyIds: string[];

  // Context Types
  contextTypeIds: string[];

  // Temporal
  numberOfCadences: number | null; // 1=Last Week, 4=Last Month, null=All

  // Location (Intent Signal Origin)
  location: {
    approach: "regions" | "countries" | "states";
    countryAlpha2s: string[];
    excludedCountryAlpha2s: string[];
    regionNames: string[];
    stateIds: string[];
  };

  // UI Cache (not persisted to API)
  topicsCache: CachedTopic[];
};

export const defaultIntentSlice: IntentSlice = {
  topicIds: [],
  topicsInclusionMethod: "ANY_PRESENT",
  collectionId: null,
  collectionName: null,
  signalScore: "HIGH",
  buyersJourneyIds: [],
  contextTypeIds: [],
  numberOfCadences: 4,
  location: {
    approach: "regions",
    countryAlpha2s: [],
    excludedCountryAlpha2s: [],
    regionNames: [],
    stateIds: [],
  },
  topicsCache: [],
};
